// The three messages the Notebook puts in the composer (#1869). Their wording is
// a contract with rule 16 of the agent's instructions
// (agent-runner/starter/CLAUDE.md): the agent reads the cell ids and the
// datasets from these lines, so change both together.
import type { NotebookCell } from './notebookTypes'

export interface SpecAxes {
  side: string
  top: string
  filter: string
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '')
const oneLine = (s: string) => s.replace(/\s+/g, ' ').trim()

/** The spec's side, top and filter, with the person's unsent edit laid over them. */
export function specAxes(cell: NotebookCell): SpecAxes {
  let spec: Record<string, unknown> = {}
  try {
    const parsed = JSON.parse(str(cell.data.spec) || '{}')
    if (parsed && typeof parsed === 'object') spec = parsed as Record<string, unknown>
  } catch {
    // A spec that does not parse has no axes to show.
  }
  return {
    side: cell.edit?.side ?? str(spec.side),
    top: cell.edit?.top ?? str(spec.top),
    filter: cell.edit?.filter ?? str(spec.filter),
  }
}

const tabularHead = (cell: NotebookCell) =>
  `- cell ${cell.id} (${cell.kind} "${oneLine(cell.title)}", job: ${str(cell.data.job)}):`
const queryHead = (cell: NotebookCell) =>
  `- cell ${cell.id} (query "${oneLine(cell.title)}" on ${str(cell.data.connection_name)}):`
const axesText = (a: SpecAxes) => `side=${a.side}; top=${a.top}; filter=${a.filter}`
const sqlBlock = (sql: string) => `\`\`\`sql\n${sql.trim()}\n\`\`\``

const hasEdit = (cell: NotebookCell) => !!cell.edit && Object.keys(cell.edit).length > 0

/** A note with something written in it. */
export const hasNoteText = (cell: NotebookCell) => cell.kind === 'note' && oneLine(cell.text ?? '') !== ''

/** A written note that Send changes has not sent yet (ask-1772: a note is sent once). */
export const hasUnsentNote = (cell: NotebookCell) => hasNoteText(cell) && !cell.sent

/** One line per written note, naming the cell above it (the last non-note cell), in Notebook order. */
function noteLines(cells: NotebookCell[], include: (cell: NotebookCell) => boolean): Map<string, string> {
  const lines = new Map<string, string>()
  let above: string | null = null
  for (const cell of cells) {
    if (cell.kind !== 'note') {
      // A script cell has no id the agent knows: a note below it is about its composite.
      above = cell.kind === 'script' ? (cell.sourceCellId ?? above) : cell.id
      continue
    }
    if (!hasNoteText(cell) || !include(cell)) continue
    lines.set(cell.id, `- note${above ? ` on cell ${above}` : ''}: ${oneLine(cell.text ?? '')}`)
  }
  return lines
}

/** "Notebook changes:" with one line per cell the person edited and per note they wrote and have not sent; '' when none. */
export function buildChangesMessage(cells: NotebookCell[]): string {
  const notes = noteLines(cells, (cell) => !cell.sent)
  const lines: string[] = []
  for (const cell of cells) {
    const note = notes.get(cell.id)
    if (note) {
      lines.push(note)
    } else if (!hasEdit(cell)) {
      continue
    } else if (cell.kind === 'table' || cell.kind === 'chart') {
      lines.push(`${tabularHead(cell)} ${axesText(specAxes(cell))}`)
    } else if (cell.kind === 'query' && cell.edit?.sql !== undefined) {
      lines.push(`${queryHead(cell)}\n${sqlBlock(cell.edit.sql)}`)
    }
  }
  return lines.length ? `Notebook changes:\n${lines.join('\n')}` : ''
}

/** "Build a web app from these Notebook cells:" naming the ticked cells, in Notebook order. */
export function buildWebappMessage(cells: NotebookCell[], tickedIds: string[]): string {
  const ticked = new Set(tickedIds)
  const notes = noteLines(cells, (cell) => ticked.has(cell.id))
  const lines: string[] = []
  for (const cell of cells) {
    if (!ticked.has(cell.id)) continue
    // A file a later change wrote over no longer holds what the cell shows.
    const fileHolds = !cell.fileReplaced
    switch (cell.kind) {
      case 'table':
      case 'chart':
        lines.push(
          cell.datasetName && !hasEdit(cell) && fileHolds
            ? `${tabularHead(cell)} data/${cell.datasetName}.json`
            : `${tabularHead(cell)} ${axesText(specAxes(cell))}`,
        )
        break
      case 'composite': {
        const file = `data/${cell.datasetName ?? ''}.json`
        lines.push(
          `- cell ${cell.id} (composite "${oneLine(cell.title)}"): ${file}` +
          (fileHolds ? '' : ' now holds a change the person did not keep; compose this cell again as it was first made, under a new dataset name, and use that'),
        )
        break
      }
      case 'query': {
        const saved = str(cell.data.saved_path)
        lines.push(
          saved && cell.edit?.sql === undefined && fileHolds
            ? `${queryHead(cell)} ${saved}`
            : `${queryHead(cell)} not saved yet; SQL:\n${sqlBlock(cell.edit?.sql ?? str(cell.data.sql))}`,
        )
        break
      }
      case 'note': {
        const note = notes.get(cell.id)
        if (note) lines.push(note)
        break
      }
    }
  }
  return [
    'Build a web app from these Notebook cells:',
    ...lines,
    'Use only these datasets. Validate every composite with validate_provenance before the app is registered.',
  ].join('\n')
}

/**
 * "About Notebook cell <id> (...):" then the person's question, for one cell
 * (the Ask button). A script cell has no id the agent knows, so it asks about
 * the composite it built. An unsent edit follows the question. '' for a blank
 * question, a note or a cell not in the Notebook.
 */
export function buildAskMessage(cells: NotebookCell[], cellId: string, question: string): string {
  const q = oneLine(question)
  let cell = cells.find((c) => c.id === cellId)
  if (cell?.kind === 'script') cell = cells.find((c) => c.id === cell!.sourceCellId)
  if (!q || !cell || cell.kind === 'note' || cell.kind === 'script') return ''
  const about = (what: string) => `About Notebook cell ${cell!.id} (${what}):\n${q}`
  const editNote = 'My edit, not run by the agent yet:'
  switch (cell.kind) {
    case 'table':
    case 'chart':
      return about(`${cell.kind} "${oneLine(cell.title)}", job: ${str(cell.data.job)}`) +
        (hasEdit(cell) ? `\n${editNote} ${axesText(specAxes(cell))}` : '')
    case 'query':
      return about(`query "${oneLine(cell.title)}" on ${str(cell.data.connection_name)}`) +
        (cell.edit?.sql !== undefined ? `\n${editNote}\n${sqlBlock(cell.edit.sql)}` : '')
    case 'composite':
      return about(`composite "${oneLine(cell.title)}", data/${cell.datasetName ?? ''}.json`)
    default:
      return ''
  }
}
